import { Minus, Plus } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import { FilterCombo, type FilterComboOption } from '@/components/ui/filter-combo';
import { Panel } from '@/components/ui/panel';
import { filterTriggerLabel } from '@/lib/filter-trigger-label';
import {
  MAX_TOP_N,
  TOP_N_ALL,
  decrementTopN,
  incrementTopN,
  topNLabel,
  type CurrentFilterStats,
  type DistributionBounds,
  type SupplierOption,
  type TopNSetting,
} from '@/lib/analysis-fusion';
import { formatEurOrDash } from '@/lib/rank-labels';

// =============================================================================
// analysis-sidebar.tsx — Colonne latérale FIXE (340 px) de la page Analyse fusionnée (lot U1).
// =============================================================================
// UX v1.3 §8.5 + maquette 2026-09-17 : la colonne droite porte les CONTRÔLES et la SYNTHÈSE —
//   1. FILTRES : menus Profil · Sous-famille · Fournisseur + stepper TOP N (INACTIF par défaut depuis
//      U1-bis) + « Réinitialiser » ;
//   2. FILTRE COURANT : trois chiffres (couples · exploitables · fournisseurs) + les indicateurs
//      P10…MAX du filtre (DBU §2.6 « Affichage des P10, P25, P50, P75, P90, MIN, MAX pour le filtre
//      courant ») + la ligne qui explique un zéro. Depuis U1-bis, ces chiffres portent sur les lignes
//      RÉELLEMENT AFFICHÉES du ranking (chips Tag et TOP N compris) — retour GATE_HUMAN 2026-09-18 ;
//   3. EXPORT · FILTRE COURANT : deux cases cochées par défaut + XLSX (1 classeur, 2 feuilles) + CSV
//      (2 téléchargements successifs) + le pied de journal ;
//   4. PANEL : « Figer le panel… » (confirmation forte portée par la page) et l'état du dernier figement.
//
// Les filtres n'ont AUCUN état local : ils LISENT les props (état d'URL) et NOTIFIENT la page
// (RULE_REF_006 — aucune donnée métier en localStorage, une vue filtrée est partageable telle quelle).
//
// LOT F1 (GATE_HUMAN 2026-09-21, verbatim : « filtre en style google sheet avec une liste déroulante
// autofiltrante » ; arbitrages « sélection multiple par cases à cocher » et « liste défilante
// complète ») : les trois menus à choix UNIQUE deviennent des `FilterCombo` MULTI-valeurs. Une liste
// vide vaut « tous » — le paramètre d'URL disparaît alors, il n'existe aucune valeur sentinelle.
// L'état du filtre est dit EN TEXTE par le déclencheur (« Fournisseur · 3 sélectionnés »), jamais par
// la seule couleur (WCAG 1.4.1 / ST-NFR-03).

export interface AnalysisFiltersSidebarProps {
  readonly profileCodes: readonly string[];
  readonly profileOptions: readonly string[];
  readonly onChangeProfiles: (values: readonly string[]) => void;
  readonly sousFamilles: readonly string[];
  readonly sousFamilleOptions: readonly string[];
  readonly onChangeSousFamilles: (values: readonly string[]) => void;
  readonly supplierSirets: readonly string[];
  readonly supplierOptions: readonly SupplierOption[];
  readonly onChangeSuppliers: (values: readonly string[]) => void;
  /** Réglage TOP N courant (`'tous'` = INACTIF, aucune limitation). */
  readonly topN: TopNSetting;
  readonly onChangeTopN: (value: TopNSetting) => void;
  readonly onReset: () => void;
  readonly hasActiveFilters: boolean;
}

export function AnalysisFiltersPanel({
  profileCodes,
  profileOptions,
  onChangeProfiles,
  sousFamilles,
  sousFamilleOptions,
  onChangeSousFamilles,
  supplierSirets,
  supplierOptions,
  onChangeSuppliers,
  topN,
  onChangeTopN,
  onReset,
  hasActiveFilters,
}: AnalysisFiltersSidebarProps): React.JSX.Element {
  // Options des trois listes. Le fournisseur porte sa raison sociale EN LIBELLÉ et son SIRET en
  // complément (`hint`) : les deux sont cherchables, et deux homonymes restent distinguables.
  const supplierComboOptions = React.useMemo(
    (): readonly FilterComboOption[] =>
      supplierOptions.map((supplier) => ({
        value: supplier.supplierSiret,
        label:
          supplier.raisonSociale === '' ? supplier.supplierSiret : supplier.raisonSociale,
        hint: supplier.supplierSiret,
      })),
    [supplierOptions],
  );
  /** Libellé AFFICHABLE d'un SIRET coché (raison sociale si connue, sinon le SIRET lui-même). */
  const supplierLabels = supplierSirets.map(
    (siret) =>
      supplierComboOptions.find((option) => option.value === siret)?.label ?? siret,
  );

  return (
    <Panel
      headingId="analysis-filters-heading"
      title="Filtres"
      aside={
        <button
          type="button"
          onClick={onReset}
          disabled={!hasActiveFilters}
          className="rounded text-xs font-medium text-foreground underline underline-offset-4 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:text-muted-foreground disabled:no-underline"
        >
          Réinitialiser
        </button>
      }
    >
      <div className="space-y-3 px-6 py-4">
        <FilterRow label="Profil">
          <FilterCombo
            triggerLabel={filterTriggerLabel('Profil', profileCodes)}
            groupLabel="Profil"
            options={profileOptions.map((code) => ({ value: code, label: code }))}
            selected={profileCodes}
            onChange={onChangeProfiles}
            searchPlaceholder="Rechercher un profil…"
            align="end"
          />
        </FilterRow>
        <FilterRow label="Sous-famille">
          <FilterCombo
            triggerLabel={filterTriggerLabel('Sous-famille', sousFamilles, 'toutes')}
            groupLabel="Sous-famille"
            options={sousFamilleOptions.map((value) => ({ value, label: value }))}
            selected={sousFamilles}
            onChange={onChangeSousFamilles}
            searchPlaceholder="Rechercher une sous-famille…"
            align="end"
          />
        </FilterRow>
        <FilterRow label="Fournisseur">
          <FilterCombo
            triggerLabel={filterTriggerLabel('Fournisseur', supplierLabels)}
            groupLabel="Fournisseur"
            options={supplierComboOptions}
            selected={supplierSirets}
            onChange={onChangeSuppliers}
            searchPlaceholder="Rechercher un fournisseur…"
            align="end"
          />
        </FilterRow>

        {/* Stepper TOP N — réglage modifiable (décision 5), INACTIF par défaut (U1-bis) : « − » descend
            jusqu'à « tous » (aucune limitation) et « + » repart à 1, 2, 3… */}
        <div className="space-y-1.5 border-t border-border pt-3">
          <div className="flex items-center justify-between gap-2">
            <span id="analysis-topn-label" className="text-sm font-medium">
              TOP N par couple
            </span>
            <div
              role="group"
              aria-labelledby="analysis-topn-label"
              className="flex items-center gap-1"
            >
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-label="Diminuer le TOP N"
                disabled={topN === TOP_N_ALL}
                className="size-8"
                onClick={() => onChangeTopN(decrementTopN(topN))}
              >
                <Minus aria-hidden="true" className="size-3.5" />
              </Button>
              <span
                aria-live="polite"
                className="min-w-12 text-center font-mono text-sm tabular-nums"
              >
                {topNLabel(topN)}
              </span>
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-label="Augmenter le TOP N"
                disabled={topN !== TOP_N_ALL && topN >= MAX_TOP_N}
                className="size-8"
                onClick={() => onChangeTopN(incrementTopN(topN))}
              >
                <Plus aria-hidden="true" className="size-3.5" />
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            {topN === TOP_N_ALL
              ? 'Réglage inactif : toutes les lignes de chaque couple Profil × Séniorité sont affichées. « + » limite le ranking aux TJM les plus bas.'
              : `Garde les ${String(topN)} TJM les plus bas de chaque couple Profil × Séniorité dans le ranking. « − » redescend jusqu’à « tous » (réglage inactif).`}{' '}
            Aucun effet sur la card Distribution, et le rang reste attribué à la main.
          </p>
        </div>
      </div>
    </Panel>
  );
}

/**
 * Ligne de la colonne de filtres : le NOM du filtre à gauche, son déclencheur à droite. Ne porte que
 * la mise en page — le contrôle (liste autofiltrante) et son état sont ceux de l'appelant.
 */
function FilterRow({
  label,
  children,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-sm font-medium">{label}</span>
      {children}
    </div>
  );
}

export interface AnalysisCurrentFilterPanelProps {
  readonly stats: CurrentFilterStats;
  readonly bounds: DistributionBounds;
  /** Nombre de lignes du ranking effectivement affichées — ce sur quoi portent TOUS les chiffres. */
  readonly rankingRowCount: number;
  /** Filtres actifs à nommer sous les chiffres (« Profil DEV-FULLSTACK », « Tag Rang A »…). */
  readonly activeFilterLabels: readonly string[];
}

/**
 * Panneau « Filtre courant » : trois chiffres + indicateurs MIN…MAX + explication d'un zéro.
 *
 * RETOUR GATE_HUMAN 2026-09-18 (verbatim) : « La distribution affichée doit se baser sur la base des
 * éléments filtrés, à travers de la section Filtres et des filtres présents dans la section Ranking
 * Nominatif. » Les chiffres ET les bornes sont donc calculés sur les couples RÉELLEMENT AFFICHÉS par le
 * ranking, et le périmètre est ÉCRIT EN CLAIR sous les chiffres (jamais laissé à deviner).
 */
export function AnalysisCurrentFilterPanel({
  stats,
  bounds,
  rankingRowCount,
  activeFilterLabels,
}: AnalysisCurrentFilterPanelProps): React.JSX.Element {
  const hasZero =
    stats.couples === 0 || stats.exploitables === 0 || stats.suppliers === 0;
  return (
    <Panel headingId="analysis-current-filter-heading" title="Filtre courant">
      <dl className="grid grid-cols-3 gap-2 px-6 py-4 text-center">
        <Figure label="couples" value={stats.couples} />
        <Figure label="exploitables" value={stats.exploitables} />
        <Figure
          label={stats.suppliers > 1 ? 'fournisseurs' : 'fournisseur'}
          value={stats.suppliers}
        />
      </dl>
      {/* Périmètre ÉCRIT EN CLAIR : les chiffres et les bornes portent sur ce que le ranking affiche. */}
      <p className="px-6 pb-4 text-xs text-muted-foreground">
        Calculé sur les{' '}
        <span className="font-mono tabular-nums text-foreground">
          {String(rankingRowCount)}
        </span>{' '}
        ligne{rankingRowCount > 1 ? 's' : ''} affichée{rankingRowCount > 1 ? 's' : ''} du ranking
        nominatif{' '}
        {activeFilterLabels.length === 0
          ? '(aucun filtre actif : tout le périmètre de la campagne).'
          : `(${activeFilterLabels.join(' · ')}).`}
      </p>
      <dl className="space-y-1 border-t border-border px-6 py-4 text-xs">
        {(
          [
            ['MIN', bounds.minEur],
            ['P10', bounds.p10Eur],
            ['P25', bounds.p25Eur],
            ['P50', bounds.p50Eur],
            ['P75', bounds.p75Eur],
            ['P90', bounds.p90Eur],
            ['MAX', bounds.maxEur],
          ] as const
        ).map(([label, value]) => (
          <div key={label} className="flex items-center justify-between gap-3">
            <dt className="font-mono uppercase tracking-wider text-muted-foreground">
              {label}
            </dt>
            <dd className="font-medium tabular-nums">{formatEurOrDash(value)}</dd>
          </div>
        ))}
      </dl>
      {hasZero ? (
        <p role="status" className="border-t border-border px-6 py-3 text-xs text-muted-foreground">
          Un zéro signifie qu’aucun couple du filtre courant n’atteint le seuil de K-anonymity, ou
          qu’aucun fournisseur du filtre n’a répondu : ce n’est pas un montant nul.
        </p>
      ) : (
        <p className="border-t border-border px-6 py-3 text-xs text-muted-foreground">
          Médiane par borne sur les couples exploitables RÉELLEMENT affichés (filtres de la section
          Filtres, chips Tag du ranking et réglage TOP N compris). Un zéro signalerait un filtre sans
          couple exploitable, jamais un montant nul.
        </p>
      )}
    </Panel>
  );
}

// --- 3. EXPORT · FILTRE COURANT (deux cases, décision 12) -----------------------------------------

/** Les deux tableaux exportables — la case cochée porte la sémantique de destinataire. */
export interface AnalysisExportSelection {
  readonly distribution: boolean;
  readonly ranking: boolean;
}

export interface AnalysisExportPanelProps {
  /** Compteur de la case « Distribution anonymisée » (couples du filtre courant). */
  readonly distributionCount: number;
  /** Compteur de la case « Ranking nominatif » (lignes du filtre courant). */
  readonly rankingCount: number;
  readonly onExport: (
    format: 'csv' | 'xlsx',
    selection: AnalysisExportSelection,
  ) => void;
  /** Nombre d'entrées du journal (pied de card) ; le journal complet est en pied de page. */
  readonly journalCount: number;
  /** Date de la dernière entrée de journal (ISO 8601) ; `undefined` si aucune. */
  readonly journalLastAt: string | undefined;
}

/**
 * Card « Export · filtre courant » : DEUX cases cochées par défaut, un bouton XLSX (UN classeur, UNE
 * feuille par case) et un bouton CSV (UN fichier par case ⇒ DEUX téléchargements successifs quand les
 * deux cases sont cochées). Aucune sélection de colonnes, aucune option d'anonymisation (décision 12).
 * Au moins une case doit rester cochée, sinon l'export est refusé (boutons désactivés).
 */
export function AnalysisExportPanel({
  distributionCount,
  rankingCount,
  onExport,
  journalCount,
  journalLastAt,
}: AnalysisExportPanelProps): React.JSX.Element {
  // Préférence d'export IN-MEMORY (ce n'est pas une donnée métier — RULE_REF_006 : aucun localStorage).
  const [selection, setSelection] = React.useState<AnalysisExportSelection>({
    distribution: true,
    ranking: true,
  });
  const fileCount = (selection.distribution ? 1 : 0) + (selection.ranking ? 1 : 0);
  const nothingChecked = fileCount === 0;

  return (
    <Panel
      headingId="analysis-export-heading"
      title="Export"
      aside={<span>filtre courant</span>}
    >
      <fieldset className="space-y-2 px-6 py-4">
        <legend className="sr-only">Tableaux à exporter</legend>
        <label htmlFor="export-distribution" className="flex items-center gap-2 text-sm">
          <input
            id="export-distribution"
            type="checkbox"
            className="size-4 rounded border-input"
            checked={selection.distribution}
            onChange={(event) =>
              setSelection((prev) => ({ ...prev, distribution: event.target.checked }))
            }
          />
          <span>
            Distribution anonymisée{' '}
            <span className="font-mono tabular-nums text-muted-foreground">
              ({String(distributionCount)} couple{distributionCount > 1 ? 's' : ''})
            </span>
          </span>
        </label>
        <label htmlFor="export-ranking" className="flex items-center gap-2 text-sm">
          <input
            id="export-ranking"
            type="checkbox"
            className="size-4 rounded border-input"
            checked={selection.ranking}
            onChange={(event) =>
              setSelection((prev) => ({ ...prev, ranking: event.target.checked }))
            }
          />
          <span>
            Ranking nominatif{' '}
            <span className="font-mono tabular-nums text-muted-foreground">
              ({String(rankingCount)} ligne{rankingCount > 1 ? 's' : ''})
            </span>
          </span>
        </label>
        {nothingChecked ? (
          <p role="status" className="text-xs text-destructive">
            Cochez au moins un tableau : sans case cochée, l’export est refusé.
          </p>
        ) : null}
      </fieldset>

      <div className="flex flex-wrap items-center gap-2 px-6">
        <Button
          type="button"
          disabled={nothingChecked}
          className="border border-foreground bg-foreground text-background hover:bg-foreground/90"
          onClick={() => onExport('xlsx', selection)}
        >
          Exporter XLSX
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={nothingChecked}
          onClick={() => onExport('csv', selection)}
        >
          CSV
        </Button>
      </div>
      <p className="px-6 pt-2 text-xs text-muted-foreground">
        Un fichier par tableau (feuilles séparées en XLSX).
        {fileCount === 2
          ? ' En CSV, deux fichiers sont téléchargés l’un après l’autre : le navigateur peut demander l’autorisation des téléchargements multiples.'
          : ''}
      </p>

      <p className="mt-3 border-t border-border px-6 py-3 text-xs text-muted-foreground">
        Journal des exports :{' '}
        {journalCount === 0
          ? 'aucune entrée'
          : `${String(journalCount)} entrée${journalCount > 1 ? 's' : ''}`}
        {journalLastAt !== undefined ? (
          <>
            {' '}
            · dernière le{' '}
            <time dateTime={journalLastAt} className="tabular-nums">
              {journalLastAt.slice(0, 10)}
            </time>
          </>
        ) : null}
        . Chaque export est journalisé pour l’audit.
      </p>
    </Panel>
  );
}

// --- 4. PANEL : figement + état de la dernière version figée --------------------------------------

export interface AnalysisPanelActionsProps {
  /** Binômes RETENUS (A + B + PROSPECT) du filtre courant. */
  readonly retainedCount: number;
  /** Binômes affichés (dénominateur « retenus sur candidats », DBU §2.6). */
  readonly coupleCount: number;
  /** Dernière version figée (numéro, date ISO, post-publication) ; `undefined` si aucune. */
  readonly latestFreeze:
    | { readonly versionNumber: number; readonly frozenAt: string; readonly postPublication: boolean }
    | undefined;
  readonly onRequestFreeze: () => void;
  readonly freezePending: boolean;
  readonly freezeRelayed: boolean;
  readonly freezeErrorMessage: string | undefined;
}

/**
 * Card « Panel » : l'action « Figer le panel… » (confirmation FORTE portée par la page) et l'état du
 * dernier figement. Décision 8 : le figement et l'historique des versions figées sont CONSERVÉS ; le
 * livrable est produit au seul format XLSX (aucun PDF n'est plus produit ni proposé — lot A3).
 */
export function AnalysisPanelActions({
  retainedCount,
  coupleCount,
  latestFreeze,
  onRequestFreeze,
  freezePending,
  freezeRelayed,
  freezeErrorMessage,
}: AnalysisPanelActionsProps): React.JSX.Element {
  return (
    <Panel
      headingId="analysis-panel-actions-heading"
      title="Panel"
      aside={
        <span>
          {latestFreeze === undefined
            ? 'non figé'
            : `figée v${String(latestFreeze.versionNumber)}${latestFreeze.postPublication ? ' · post-publication' : ''}`}
        </span>
      }
    >
      <div className="space-y-3 px-6 py-4">
        <p className="text-xs text-muted-foreground">
          <span className="font-mono tabular-nums text-foreground">
            {String(retainedCount)} / {String(coupleCount)}
          </span>{' '}
          binôme{coupleCount > 1 ? 's' : ''} retenu{retainedCount > 1 ? 's' : ''} (Rang A, Rang B ou
          Prospect) sur les candidats du filtre courant.
        </p>
        {latestFreeze !== undefined ? (
          <p className="text-xs text-muted-foreground">
            Dernier figement : v{String(latestFreeze.versionNumber)} du{' '}
            <time dateTime={latestFreeze.frozenAt} className="tabular-nums">
              {latestFreeze.frozenAt.slice(0, 10)}
            </time>{' '}
            · livrable XLSX. L’historique complet est en pied de page.
          </p>
        ) : null}
        {/* Le libellé porte « … » : convention d'un déclencheur OUVRANT une confirmation forte (le
            bouton de CONFIRMATION de la modale porte « Figer le panel » sec — deux noms distincts). */}
        <Button type="button" disabled={freezePending} onClick={onRequestFreeze}>
          {freezePending ? 'Transmission…' : 'Figer le panel…'}
        </Button>
        {freezeRelayed ? (
          <p role="status" className="text-xs text-muted-foreground">
            Demande de figement transmise. La vue reste modifiable : vous pouvez re-figer après
            ajustement.
          </p>
        ) : null}
        {freezeErrorMessage !== undefined ? (
          <p role="alert" className="text-xs text-destructive">
            {freezeErrorMessage}
          </p>
        ) : null}
      </div>
    </Panel>
  );
}

function Figure({
  label,
  value,
}: {
  readonly label: string;
  readonly value: number;
}): React.JSX.Element {
  // `flex-col-reverse` : le chiffre s'affiche AU-DESSUS de son libellé, mais l'ordre du DOM reste
  // <dt> puis <dd> (contrainte des listes de définitions, axe `definition-list`).
  return (
    <div className="flex flex-col-reverse">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-serif text-2xl font-medium tabular-nums">{String(value)}</dd>
    </div>
  );
}
